import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";

import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center justify-center rounded-xs border px-2 py-0.5 text-xs font-semibold w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/45 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden uppercase tracking-[0.12em]",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground dark:bg-neutral-50 dark:text-neutral-900 [a&]:hover:bg-secondary/90",
        destructive:
          "border-destructive/20 bg-destructive-surface text-destructive-foreground [a&]:hover:opacity-85 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40",
        warning:
          "border-warning/20 bg-warning-surface text-warning-foreground [a&]:hover:opacity-85 focus-visible:ring-warning/20 dark:focus-visible:ring-warning/40",
        outline:
          "border-border bg-background/70 text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        ghost:
          "bg-muted/75 dark:bg-neutral-800 text-foreground border-transparent [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        draft:
          "border-transparent bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-200",
        pending_review:
          "border-warning/20 bg-warning-surface text-warning-foreground",
        approved:
          "border-success/20 bg-success-surface text-success-foreground",
        rejected:
          "border-destructive/20 bg-destructive-surface text-destructive-foreground",
        required:
          "border-destructive/20 bg-destructive-surface text-destructive-foreground",
        closed: "border-border/60 bg-muted text-muted-foreground",
        high: "border-destructive/20 bg-destructive-surface text-destructive-foreground",
        medium: "border-warning/20 bg-warning-surface text-warning-foreground",
        low: "border-success/20 bg-success-surface text-success-foreground",
        gray: "border-border/60 bg-muted text-muted-foreground",
        global: "border-success/20 bg-success-surface text-success-foreground",
        site: "border-info/20 bg-info-surface text-info-foreground",
      },
      size: {
        xs: "h-4 text-[10px] px-1",
        sm: "h-[18px] text-[10px] px-1.5",
        md: "h-5 text-[10px] px-1.5",
        lg: "h-[22px] text-[11px] px-2",
        xl: "h-6 text-[11px] px-2.5",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  }
);

function Badge({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : "span";

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant, size }), className)}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
